import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LayerHost } from '@fluentui/react';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { SpecialtyCreateCommand, SpecialtyUpdateCommand } from '../../../models/Entities/Specialties/SpecialtyModels';
import { SnomedItem } from '../../../models/Entities/Snomed/SnomedModels';
import SnomedSelector from '../../../commons/component/Inputs/SnomedSelector/SnomedSelector.Component';
import * as ownFunctions from './MedicalSpecialtiesForm.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const MedicalSpecialtiesFormComponent = (props: ownFunctions.Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();
  const [name, setName] = useState<string>('');
  const [snomedCode, setSnomedCode] = useState<string>('');
  const [snomedDisplayName, setSnomedDisplayName] = useState<string>('');
  const [snomedName, setSnomedName] = useState<string>('');
  const [t] = useTranslation();

  useEffect(() => {
    if (props.specialty) {
      setName(props.specialty.name || '');
      setSnomedCode(props.specialty.snomedCode || '');
      setSnomedDisplayName(props.specialty.snomedName || props.specialty.snomedCode || '');
      setSnomedName(props.specialty.snomedName || '');
    } else {
      resetForm();
    }
  }, [props.specialty]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      props.GetAllSpecialties();
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setName('');
    setSnomedCode('');
    setSnomedDisplayName('');
    setSnomedName('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];
    if (!name.trim()) {
      errors.push(t('medicalSpecialties.form.validation.name', { defaultValue: 'El nombre es obligatorio' }));
    }
    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    if (props.specialty) {
      const payload: SpecialtyUpdateCommand = {
        id: props.specialty.id,
        name: name.trim(),
        snomedCode: snomedCode.trim() || null,
        snomedName: snomedName.trim() || null
      };
      props.UpdateSpecialty(payload);
    } else {
      const payload: SpecialtyCreateCommand = {
        name: name.trim(),
        snomedCode: snomedCode.trim() || null,
        snomedName: snomedName.trim() || null
      };
      props.CreateSpecialty(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const isSaving = !!(props.isAdding || props.isUpdating);

  return (
    <>
      <GuideDrawer
        open={props.isOpen}
        size="small"
        eyebrow={props.specialty?.name || undefined}
        title={t('medicalSpecialties.form.title', { defaultValue: 'Especialidad médica' })}
        onDismiss={handleCancel}
        preventDismiss={isSaving}
        testId="medical-specialty-drawer"
        secondaryAction={{ label: t('medicalSpecialties.form.footer.cancel', { defaultValue: 'Cancelar' }) }}
        primaryAction={{
          label: t('medicalSpecialties.form.footer.save', { defaultValue: 'Guardar' }),
          onClick: handleSave,
          loading: isSaving,
          disabled: !name.trim() || !snomedCode.trim() || !snomedName.trim()
        }}
      >
        {submitted && validation && !validation.success && (
          <GuideMessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>
                {t('medicalSpecialties.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </MessageBarBody>
          </GuideMessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <GuideDrawerForm onSubmit={handleSave}>
          <GuideField label={t('medicalSpecialties.form.snomed', { defaultValue: 'Código SNOMED' })}>
            <SnomedSelector
              value={snomedCode}
              displayText={snomedDisplayName}
              layerHostId="snomed-callout-layer-host"
              onSelect={(item: SnomedItem) => {
                setSnomedCode(item.id);
                setSnomedDisplayName(item.name);
                setSnomedName(item.name);
                setName(item.name);
              }}
              onClear={() => {
                setSnomedCode('');
                setSnomedDisplayName('');
                setSnomedName('');
              }}
            />
          </GuideField>

          <GuideField label={t('medicalSpecialties.form.snomedName', { defaultValue: 'Nombre SNOMED' })}>
            <Input value={snomedName} disabled />
          </GuideField>

          <GuideField
            label={t('medicalSpecialties.form.name', { defaultValue: 'Nombre' })}
            required
            error={
              submitted && !name.trim()
                ? t('medicalSpecialties.form.validation.name', { defaultValue: 'El nombre es obligatorio' })
                : undefined
            }
          >
            <Input value={name} onChange={(_, data) => setName(data.value || '')} />
          </GuideField>
        </GuideDrawerForm>
      </GuideDrawer>
      {props.isOpen && createPortal(
        <LayerHost id="snomed-callout-layer-host" style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999999 }} />,
        document.body
      )}
    </>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(MedicalSpecialtiesFormComponent as any);
